// Shared design tokens for all redesigned pages.
// Import these instead of duplicating the dt object inline.

export const dt = {
  pageBg: "#F0F2F7",
  pageDotColor: "rgba(52, 152, 219, 0.04)",
  cardBg: "#FFFFFF",
  cardBorder: "#E5E7EB",
  cardShadow: "0 1px 3px rgba(0,0,0,0.04), 0 4px 12px rgba(0,0,0,0.02)",
  cardShadowHover: "0 4px 16px rgba(0,0,0,0.06), 0 1px 4px rgba(0,0,0,0.04)",

  headerBg: "#FAFBFC",
  rowBorder: "#F3F4F6",
  rowHover: "#F6F9FD",

  textPrimary: "#1a1a2e",
  textSecondary: "#6B7280",
  textMuted: "#9CA3AF",
  textLink: "var(--accent, #3498DB)",

  blue: "var(--accent, #3498DB)",
  blueHover: "var(--accent-hover, #2980B9)",
  blueGlow: "var(--accent-glow, rgba(52, 152, 219, 0.12))",
  blueSoft: "var(--accent-soft, rgba(52, 152, 219, 0.06))",

  /** Darker gradient end — used in button gradients, shimmer bars */
  gradientDark: "var(--accent-gradient-dark, #2E86C1)",
  /** Lighter gradient end — used in lighter accent gradients */
  gradientLight: "var(--accent-gradient-light, #5DADE2)",

  inputBg: "#F4F6F9",

  roleGreen: "#27AE60",
  roleGreenBg: "rgba(39,174,96,0.08)",
  rolePurple: "#8E44AD",
  rolePurpleBg: "rgba(142,68,173,0.08)",
  amber: "#F59E0B",

  radius: "14px",
  radiusSm: "8px",
  radiusLg: "18px",
  radiusPill: "24px",

  font: 'var(--font-sans)',
  fontMono: 'var(--font-mono)',
};

/** Primary accent gradient (blue → dark blue). Use for CTA buttons. */
export const accentGradient = `linear-gradient(135deg, var(--accent, #3498DB), var(--accent-hover, #2980B9))`;
/** Reverse accent gradient (dark blue → blue). Use for hover states. */
export const accentGradientHover = `linear-gradient(135deg, var(--accent-hover, #2980B9), var(--accent, #3498DB))`;
/** Accent-to-dark gradient. Use for standard action buttons. */
export const accentGradientDark = `linear-gradient(135deg, var(--accent, #3498DB), var(--accent-gradient-dark, #2E86C1))`;
/** Dark-to-accent gradient. Use for hover on standard action buttons. */
export const accentGradientDarkHover = `linear-gradient(135deg, var(--accent-gradient-dark, #2E86C1), var(--accent, #3498DB))`;
/** Accent-to-light gradient. Use for lighter accents, logos, bars. */
export const accentGradientLight = `linear-gradient(135deg, var(--accent, #3498DB), var(--accent-gradient-light, #5DADE2))`;
/** Light-to-accent gradient. Use for hover on lighter accent elements. */
export const accentGradientLightHover = `linear-gradient(135deg, var(--accent-gradient-light, #5DADE2), var(--accent, #3498DB))`;
/** Horizontal accent shimmer bar (accent → light → transparent). */
export const accentShimmer = `linear-gradient(90deg, var(--accent, #3498DB) 0%, var(--accent-gradient-light, #5DADE2) 50%, transparent 100%)`;
/** Dark accent gradient for secondary buttons. */
export const accentGradientDeep = `linear-gradient(135deg, var(--accent-hover, #2980B9), var(--accent-gradient-dark, #2E86C1))`;

export interface StatusBadgeColorConfig {
  bg: string;
  text: string;
  border: string;
}

/** Green design tokens for Service Tracking — extends dt with green accent colors */
export const dtGreen = {
  ...dt,
  green50: "#ECFDF5",
  green100: "#D1FAE5",
  green200: "#A7F3D0",
  green300: "#6EE7B7",
  green400: "#34D399",
  green500: "#10B981",
  green600: "#059669",
  green700: "#047857",
  greenGlow: "rgba(16, 185, 129, 0.12)",
  greenSoft: "rgba(16, 185, 129, 0.06)",
  amber500: "#F59E0B",
  amberGlow: "rgba(245, 158, 11, 0.12)",
};

/** Pagination wrapper sx — forces system font on all pagination elements */
export const paginationWrapperSx = {
  "& *": { fontFamily: `${dt.font} !important` },
  "& span, & p, & div": { fontSize: "13px !important", letterSpacing: "0.1px" },
  "& .MuiIconButton-root": {
    color: dt.textSecondary,
    borderRadius: dt.radiusSm,
    transition: "all 0.2s",
    "&:hover": { backgroundColor: dt.blueGlow, color: dt.blue },
  },
};
